import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { DynamicEntityRow } from '../../../../api/strapiDynamicEntity';
import {
  parseQuotationChangeLogs,
  quotationChangeLogMetaAt,
  quotationChangeLogMetaSource,
} from './formatQuotationChangeLogs';

function formatLogTimestamp(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v);
  if (!s.trim()) return null;
  return s.slice(0, 19).replace('T', ' ');
}

export type ChangeLogDetailPanelProps = {
  row: DynamicEntityRow;
};

/** Panel detail satu baris log (tabel field / lama / baru). */
export function ChangeLogDetailPanel({ row }: ChangeLogDetailPanelProps) {
  const entries = parseQuotationChangeLogs(row.change_logs);
  const meta = quotationChangeLogMetaSource(row.change_logs);
  const metaAt = quotationChangeLogMetaAt(row.change_logs);
  const updatedAt = formatLogTimestamp(row.updatedAt);
  const createdAt = formatLogTimestamp(row.createdAt);

  return (
    <Box sx={{ py: 1, width: '100%' }} data-testid="quotation-log-detail-panel">
      <Stack spacing={1.5}>
        <Stack spacing={0.25}>
          {meta ? (
            <Typography variant="body2" color="text.secondary">
              {meta}
              {metaAt ? ` · ${metaAt.slice(0, 19).replace('T', ' ')}` : ''}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary">
            {createdAt ? `Dibuat: ${createdAt}` : null}
            {createdAt && updatedAt ? ' · ' : null}
            {updatedAt ? `Diperbarui: ${updatedAt}` : null}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'ui-monospace, monospace' }}>
            ID log: {row.documentId}
          </Typography>
        </Stack>

        {entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Tidak ada perubahan field pada entri ini.
          </Typography>
        ) : (
          <Table size="small" sx={{ '& .MuiTableCell-root': { verticalAlign: 'top' } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '34%' }}>Field</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '33%' }}>Nilai lama</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '33%' }}>Nilai baru</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((e) => (
                <TableRow key={e.field} data-testid={`quotation-log-detail-${e.field}`}>
                  <TableCell>{e.label}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{e.old}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{e.new}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Stack>
    </Box>
  );
}
